/* Patch 16 — Variation Order Queue
   ===================================
   The CFO-grade two-step posting flow for VOs:
     • Approve → contract_value updated, status='approved'
     • Preview → see Revenue/Cost/Profit/VAT/Accounts impact
     • Post-Journal → JRN issued, status='posted'
     • Reverse → audit-safe (flipped journal, status='reversed')

   Layout: 6 status cards on top, table beneath, drilldown drawer with
   journal preview + actions. */
import React, { useState, useEffect, useCallback } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import {
  Loader2, RefreshCw, FileText, Eye, CheckCircle2, RotateCcw,
  X, AlertTriangle, ArrowRight, ShieldAlert,
} from 'lucide-react';

const fmt = (n) => {
  if (n === null || n === undefined) return '—';
  return Number(n).toLocaleString('en-US', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });
};

const STATUS_META = {
  draft:        { label: 'Draft',         cls: 'bg-slate-100 text-slate-700' },
  pending:      { label: 'Pending',       cls: 'bg-amber-100 text-amber-800' },
  under_review: { label: 'Under Review',  cls: 'bg-amber-100 text-amber-800' },
  approved:     { label: 'Approved',      cls: 'bg-sky-100   text-sky-800' },
  ready_to_post:{ label: 'Ready to Post', cls: 'bg-violet-100 text-violet-800' },
  posted:       { label: 'Posted',        cls: 'bg-emerald-100 text-emerald-800' },
  reversed:     { label: 'Reversed',      cls: 'bg-rose-100 text-rose-800' },
  rejected:     { label: 'Rejected',      cls: 'bg-rose-100 text-rose-800' },
  implemented:  { label: 'Implemented',   cls: 'bg-indigo-100 text-indigo-800' },
};

export default function VOQueue() {
  const [queue, setQueue] = useState(null);
  const [rows, setRows]   = useState([]);
  const [legacy, setLegacy] = useState(null);
  const [showLegacy, setShowLegacy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [active, setActive]   = useState(null);
  const [filter, setFilter]   = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [qRes, rRes, lRes] = await Promise.all([
        axiosInstance.get('/variation-orders/queue'),
        axiosInstance.get('/variation-orders'),
        axiosInstance.get('/variation-orders/legacy-scan'),
      ]);
      setQueue(qRes.data);
      setRows(rRes.data || []);
      setLegacy(lRes.data);
    } catch (e) {
      toast.error(`Load failed: ${e.response?.data?.detail || e.message}`);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filteredRows = filter === 'all'
    ? rows
    : rows.filter(r => (r.status || '').toLowerCase() === filter);

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-5" data-testid="vo-queue-page">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 inline-flex items-center gap-2">
            <FileText className="text-sky-600" size={26} /> Variation Order Queue
          </h1>
          <p className="text-sm text-slate-600">
            Two-step posting: Approve → Preview Journal → Post Journal. Reversals are audit-safe (flipped journal, never deleted).
          </p>
        </div>
        <button
          data-testid="voq-refresh-btn"
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white rounded px-4 py-1.5 text-sm font-semibold disabled:opacity-50"
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          Refresh
        </button>
      </header>

      {queue && (
        <div className="grid grid-cols-2 md:grid-cols-7 gap-2" data-testid="voq-summary-cards">
          {[
            ['all',          'All',          rows.length,                     'bg-white border-slate-300'],
            ['draft',        'Draft',        queue.draft,                     STATUS_META.draft.cls],
            ['pending',      'Pending',      queue.pending + queue.under_review, STATUS_META.pending.cls],
            ['approved',     'Approved',     queue.approved,                  STATUS_META.approved.cls],
            ['posted',       'Posted',       queue.posted,                    STATUS_META.posted.cls],
            ['reversed',     'Reversed',     queue.reversed,                  STATUS_META.reversed.cls],
            ['implemented',  'Implemented',  queue.implemented,               STATUS_META.implemented.cls],
          ].map(([key, label, val, cls]) => (
            <button
              key={key}
              data-testid={`voq-card-${key}`}
              onClick={() => setFilter(key)}
              className={`rounded-lg p-2.5 border text-left ${cls} ${
                filter === key ? 'ring-2 ring-offset-1 ring-slate-700' : ''
              }`}
            >
              <div className="text-[9px] uppercase tracking-wider opacity-80">{label}</div>
              <div className="text-xl font-bold font-mono mt-0.5">{val ?? 0}</div>
            </button>
          ))}
        </div>
      )}

      {queue?.ready_to_post > 0 && (
        <div className="bg-violet-50 border border-violet-300 rounded-lg p-3 inline-flex items-center gap-2" data-testid="voq-ready-banner">
          <AlertTriangle size={14} className="text-violet-700" />
          <span className="text-sm text-violet-900">
            <strong>{queue.ready_to_post}</strong> approved VO(s) ready to post.
            Open each one and click <strong>Post Journal</strong>.
          </span>
        </div>
      )}

      <section className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-xs" data-testid="voq-rows-table">
          <thead>
            <tr className="bg-slate-50 text-left text-[10px] uppercase text-slate-500">
              <th className="px-3 py-2">VO #</th>
              <th className="px-3 py-2">Project</th>
              <th className="px-3 py-2">Date</th>
              <th className="px-3 py-2 text-right">Amount</th>
              <th className="px-3 py-2">Impact</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Journal</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredRows.length === 0 ? (
              <tr><td colSpan={8} className="text-center py-8 text-slate-500">No variation orders.</td></tr>
            ) : (
              filteredRows.map((r, i) => {
                const sm = STATUS_META[(r.status || '').toLowerCase()] || STATUS_META.draft;
                return (
                  <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`voq-row-${i}`}>
                    <td className="px-3 py-2 font-mono font-bold">{r.vo_number}</td>
                    <td className="px-3 py-2 max-w-[180px] truncate">{r.project_name || '—'}</td>
                    <td className="px-3 py-2 text-slate-500">{r.vo_date || '—'}</td>
                    <td className="px-3 py-2 text-right font-mono">{fmt(r.amount)}</td>
                    <td className="px-3 py-2 text-[10px] text-slate-600">{r.impact_type || '—'}</td>
                    <td className="px-3 py-2">
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${sm.cls}`}>{sm.label}</span>
                    </td>
                    <td className="px-3 py-2 font-mono text-[10px]">
                      {r.journal_no || '—'}
                      {r.reversal_journal_no && (
                        <span className="block text-rose-600">↺ {r.reversal_journal_no}</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        data-testid={`voq-open-${i}`}
                        onClick={() => setActive(r)}
                        className="inline-flex items-center gap-1 bg-slate-800 hover:bg-slate-900 text-white text-[10px] font-semibold rounded px-2 py-1"
                      >
                        Open <ArrowRight size={9} />
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </section>

      {active && (
        <VOActionDrawer
          vo={active}
          onClose={() => setActive(null)}
          onChanged={async () => { await load(); }}
        />
      )}
    </div>
  );
}


function VOActionDrawer({ vo, onClose, onChanged }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy]       = useState(null);
  const [reversalReason, setReversalReason] = useState('');

  const loadPreview = useCallback(async () => {
    if (!['approved','posted','ready_to_post'].includes(vo.status)) {
      setPreview(null);
      return;
    }
    setLoading(true);
    try {
      const r = await axiosInstance.get(
        `/variation-orders/${vo.id}/journal-preview`);
      setPreview(r.data);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(`Preview failed: ${typeof d === 'object' ? (d.error || JSON.stringify(d)) : (d || e.message)}`);
    } finally { setLoading(false); }
  }, [vo.id, vo.status]);

  useEffect(() => { loadPreview(); }, [loadPreview]);

  const action = async (label, fn) => {
    setBusy(label);
    try {
      await fn();
      toast.success(`${label} done.`);
      await onChanged();
      onClose();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(`${label} failed: ${typeof d === 'object' ? (d.error || JSON.stringify(d)) : (d || e.message)}`);
    } finally { setBusy(null); }
  };

  const sm = STATUS_META[(vo.status || '').toLowerCase()] || STATUS_META.draft;

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="vo-action-drawer">
      <button className="flex-1 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} />
      <div className="w-full max-w-2xl bg-white shadow-2xl flex flex-col">
        <header className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <div>
            <div className="text-xs uppercase text-slate-500 tracking-wider">Variation Order</div>
            <h2 className="text-lg font-bold text-slate-900">
              {vo.vo_number}
              <span className={`ml-2 text-[10px] font-bold px-1.5 py-0.5 rounded ${sm.cls}`}>{sm.label}</span>
            </h2>
          </div>
          <button data-testid="drawer-close" onClick={onClose} className="p-2 rounded-full hover:bg-slate-100">
            <X size={18} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <div className="grid grid-cols-2 gap-2 text-xs">
            <Info label="Project" value={vo.project_name} />
            <Info label="Date"    value={vo.vo_date} />
            <Info label="Amount"  value={fmt(vo.amount)} mono />
            <Info label="Impact"  value={vo.impact_type || '—'} />
          </div>
          {vo.description && (
            <div className="text-xs">
              <span className="text-slate-500">Description: </span>
              <span className="text-slate-900">{vo.description}</span>
            </div>
          )}

          {loading && (
            <div className="flex items-center gap-2 text-slate-500 text-xs">
              <Loader2 size={12} className="animate-spin" /> Loading journal preview…
            </div>
          )}

          {preview && preview.ok && (
            <section className="bg-slate-50 border border-slate-200 rounded p-3" data-testid="drawer-preview">
              <h3 className="text-xs font-bold text-slate-700 mb-2">Journal Preview</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
                <Stat label="Revenue" value={fmt(preview.impact.revenue_impact)} cls={preview.impact.revenue_impact>=0 ? 'text-emerald-700':'text-rose-700'} />
                <Stat label="Cost"    value={fmt(preview.impact.cost_impact)}    cls={preview.impact.cost_impact>=0 ? 'text-rose-700':'text-emerald-700'} />
                <Stat label="Profit"  value={fmt(preview.impact.profit_impact)}  cls={preview.impact.profit_impact>=0 ? 'text-emerald-700':'text-rose-700'} />
                <Stat label="VAT"     value={fmt(preview.impact.vat_amount)}     cls="text-slate-700" />
              </div>
              <table className="w-full text-[11px] font-mono" data-testid="drawer-preview-lines">
                <thead>
                  <tr className="bg-slate-100 text-left text-[10px] uppercase text-slate-500">
                    <th className="px-2 py-1">Account</th>
                    <th className="px-2 py-1 text-right">Debit</th>
                    <th className="px-2 py-1 text-right">Credit</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.lines.map((ln, i) => (
                    <tr key={i} className="border-t border-slate-200">
                      <td className="px-2 py-1">
                        <span className="font-bold">{ln.account_code}</span>
                        {ln.account_number && <span className="text-slate-400 ml-1">{ln.account_number}</span>}
                      </td>
                      <td className="px-2 py-1 text-right">{ln.debit ? fmt(ln.debit) : '—'}</td>
                      <td className="px-2 py-1 text-right">{ln.credit ? fmt(ln.credit) : '—'}</td>
                    </tr>
                  ))}
                  <tr className="border-t-2 border-slate-300 font-bold">
                    <td className="px-2 py-1">Totals</td>
                    <td className="px-2 py-1 text-right">{fmt(preview.total_debit)}</td>
                    <td className="px-2 py-1 text-right">{fmt(preview.total_credit)}</td>
                  </tr>
                </tbody>
              </table>
              {!preview.balanced && (
                <div className="text-rose-700 text-xs mt-2">⚠️ Unbalanced — refusing to post.</div>
              )}
            </section>
          )}

          {/* Reversal reason input when posted */}
          {vo.status === 'posted' && (
            <section className="bg-rose-50 border border-rose-200 rounded p-3">
              <h3 className="text-xs font-bold text-rose-800 mb-1">Reverse Journal</h3>
              <p className="text-[11px] text-rose-700 mb-2">
                A flipped journal will be posted. The original journal is preserved (audit-safe).
              </p>
              <input
                data-testid="reversal-reason-input"
                value={reversalReason}
                onChange={e => setReversalReason(e.target.value)}
                placeholder="Reason (required) — e.g. scope change reverted"
                className="w-full border border-rose-300 rounded px-2 py-1.5 text-xs"
              />
            </section>
          )}
        </div>

        <footer className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2 flex-wrap">
          {vo.status === 'approved' && (
            <button
              data-testid="drawer-post-btn"
              disabled={busy || !preview?.balanced}
              onClick={() => action('Post Journal',
                () => axiosInstance.post(`/variation-orders/${vo.id}/post-journal`))}
              className="px-3 py-1.5 text-xs rounded bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white font-semibold inline-flex items-center gap-1"
            >
              {busy === 'Post Journal'
                ? <Loader2 size={11} className="animate-spin" />
                : <CheckCircle2 size={11} />}
              Post Journal
            </button>
          )}
          {vo.status === 'posted' && (
            <button
              data-testid="drawer-reverse-btn"
              disabled={busy || reversalReason.trim().length < 3}
              onClick={() => action('Reverse',
                () => axiosInstance.post(`/variation-orders/${vo.id}/reverse`,
                  { reason: reversalReason }))}
              className="px-3 py-1.5 text-xs rounded bg-rose-600 hover:bg-rose-700 disabled:opacity-40 text-white font-semibold inline-flex items-center gap-1"
            >
              {busy === 'Reverse'
                ? <Loader2 size={11} className="animate-spin" />
                : <RotateCcw size={11} />}
              Reverse
            </button>
          )}
          <button
            data-testid="drawer-close-footer"
            onClick={onClose}
            className="px-3 py-1.5 text-xs rounded border border-slate-300 text-slate-700 hover:bg-slate-50"
          >
            Close
          </button>
        </footer>
      </div>
    </div>
  );
}


function LegacyScannerPanel({ legacy, show, onToggle }) {
  const CLS_COLORS = {
    revenue_increase: 'text-emerald-700 bg-emerald-50 border-emerald-200',
    revenue_decrease: 'text-amber-700  bg-amber-50  border-amber-200',
    cost_increase:    'text-rose-700   bg-rose-50   border-rose-200',
    cost_decrease:    'text-sky-700    bg-sky-50    border-sky-200',
    ambiguous:        'text-slate-700  bg-slate-100 border-slate-300',
  };
  return (
    <div className="bg-amber-50 border border-amber-300 rounded-lg p-3" data-testid="legacy-scanner-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="inline-flex items-center gap-2">
          <ShieldAlert size={16} className="text-amber-700" />
          <div>
            <div className="text-xs uppercase font-bold tracking-wider text-amber-900">
              Legacy VO Scanner (Patch 16.1)
            </div>
            <div className="text-xs text-amber-800 mt-0.5">
              <strong>{legacy.total_legacy}</strong> VO(s) without explicit impact_type.{' '}
              {legacy.risk_critical > 0 && (
                <strong className="text-rose-700">
                  {legacy.risk_critical} CRITICAL (posted with unclear intent).{' '}
                </strong>
              )}
              <span className="opacity-70">{legacy.advisory}</span>
            </div>
          </div>
        </div>
        <button
          data-testid="toggle-legacy-rows"
          onClick={onToggle}
          className="text-xs underline text-amber-900 hover:text-amber-700"
        >
          {show ? 'Hide details' : `View details (${legacy.total_legacy})`}
        </button>
      </div>
      <div className="grid grid-cols-5 gap-2 mt-3" data-testid="legacy-classification-cards">
        {Object.entries(legacy.by_classification).map(([cls, count]) => (
          <div key={cls} className={`rounded px-2 py-1.5 border ${CLS_COLORS[cls] || ''}`}>
            <div className="text-[9px] uppercase tracking-wider opacity-70">
              {cls.replace('_', ' ')}
            </div>
            <div className="text-lg font-bold font-mono mt-0.5">{count}</div>
          </div>
        ))}
      </div>
      {show && (
        <div className="mt-3 max-h-80 overflow-y-auto bg-white rounded border border-amber-200">
          <table className="w-full text-[11px]" data-testid="legacy-rows-table">
            <thead>
              <tr className="bg-amber-100 text-left text-[10px] uppercase text-amber-800 sticky top-0">
                <th className="px-2 py-1.5">VO #</th>
                <th className="px-2 py-1.5">Amount</th>
                <th className="px-2 py-1.5">Status</th>
                <th className="px-2 py-1.5">Journal</th>
                <th className="px-2 py-1.5">Suggested</th>
                <th className="px-2 py-1.5">Confidence</th>
                <th className="px-2 py-1.5">Risk</th>
                <th className="px-2 py-1.5">Why</th>
              </tr>
            </thead>
            <tbody>
              {legacy.rows.map((r, i) => (
                <tr key={r.id} className="border-t border-amber-100" data-testid={`legacy-row-${i}`}>
                  <td className="px-2 py-1 font-mono font-bold">{r.vo_number}</td>
                  <td className="px-2 py-1 font-mono">{r.amount ?? '—'}</td>
                  <td className="px-2 py-1 text-[10px]">{r.status}</td>
                  <td className="px-2 py-1 font-mono text-[10px]">{r.journal_no || '—'}</td>
                  <td className="px-2 py-1">
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded border ${CLS_COLORS[r.suggested_impact_type] || ''}`}>
                      {r.suggested_impact_type.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-2 py-1 font-mono text-[10px]">{r.confidence}</td>
                  <td className="px-2 py-1">
                    <span className={`text-[10px] font-bold px-1 rounded ${
                      r.risk === 'critical' ? 'bg-rose-600 text-white' :
                      r.risk === 'warning'  ? 'bg-amber-500 text-white' :
                      'bg-slate-200 text-slate-700'
                    }`}>{r.risk}</span>
                  </td>
                  <td className="px-2 py-1 text-[10px] text-slate-600 max-w-xs truncate">
                    {r.reasons.join(' · ')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


function Info({ label, value, mono }) {
  return (
    <div>
      <div className="text-[10px] uppercase text-slate-500">{label}</div>
      <div className={`text-xs text-slate-900 ${mono ? 'font-mono' : ''}`}>{value || '—'}</div>
    </div>
  );
}

function Stat({ label, value, cls }) {
  return (
    <div className="bg-white rounded p-2 border border-slate-200">
      <div className="text-[9px] uppercase text-slate-500">{label}</div>
      <div className={`text-base font-bold font-mono mt-0.5 ${cls}`}>{value}</div>
    </div>
  );
}
